import { ComponentType, lazy } from 'react';
import { isChunkLoadError } from './chunkLoadError';

const CHUNK_RELOAD_GUARD_KEY = 'agently-chunk-reload-at';
const CHUNK_RELOAD_GUARD_WINDOW_MS = 10_000;
// Both the reload safety timeout and the max retry delay share this ceiling.
// If you change one, reconsider the other.
const MAX_WAIT_MS = 30_000;

// NOTE: sessionStorage is tab-scoped, so each tab tracks its own reload guard
// independently. Multiple tabs opened to the same app will each attempt a
// reload on their first chunk error within the guard window.
const shouldReloadChunkError = () => {
  if (typeof window === 'undefined') return false;

  const lastReloadAt = Number(
    window.sessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY) || '0',
  );
  const now = Date.now();

  if (
    Number.isNaN(lastReloadAt) ||
    now - lastReloadAt > CHUNK_RELOAD_GUARD_WINDOW_MS
  ) {
    window.sessionStorage.setItem(CHUNK_RELOAD_GUARD_KEY, String(now));
    return true;
  }

  return false;
};

/**
 * Creates a lazy-loaded component with retry logic for failed module imports.
 * This helps handle network errors or temporary failures when loading code-split chunks.
 *
 * For ChunkLoadError (when chunk no longer exists after deployment), it automatically
 * reloads the page to fetch the new version. If the reload is blocked or does not occur,
 * the loader will reject after MAX_WAIT_MS (30 s) to prevent an unbounded pending state —
 * error boundaries should be prepared to handle this delayed rejection.
 * For other errors, it retries with exponential backoff.
 *
 * @param factory - Function that returns a promise resolving to the module
 * @param maxRetries - Maximum number of retry attempts (default: 3)
 * @param retryDelay - Delay in milliseconds between retries (default: 1000)
 * @returns Lazy-loaded React component
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  maxRetries: number = 3,
  retryDelay: number = 1000,
) {
  return lazy(async () => {
    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const module = await factory();
        return module;
      } catch (error) {
        lastError = error as Error;

        // ChunkLoadError typically means the chunk no longer exists on the server
        // (e.g., after a deployment). Reload the page to get the new version.
        if (isChunkLoadError(error)) {
          if (!shouldReloadChunkError()) {
            throw error;
          }
          console.warn(
            'Chunk load error detected, reloading page to fetch new version...',
            error,
          );
          window.location.reload();
          // The reload is asynchronous; returning a component here would cause
          // a race condition where a blank/null component renders before the
          // page unloads. We return a promise intentionally expected not to
          // resolve. As a safety mechanism in case the reload is blocked or
          // fails, we reject after MAX_WAIT_MS to avoid an unbounded pending
          // state.
          return new Promise<never>((_, reject) => {
            setTimeout(() => {
              reject(
                new Error(
                  'Page reload did not occur after chunk load error. Aborting infinite pending state.',
                ),
              );
            }, MAX_WAIT_MS);
          });
        }

        // If this is the last attempt, throw the error
        if (attempt === maxRetries) {
          throw lastError;
        }

        // Wait before retrying (exponential backoff with a maximum cap)
        const delay = Math.min(MAX_WAIT_MS, retryDelay * Math.pow(2, attempt));
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }

    // This should never be reached, but TypeScript needs it
    throw lastError || new Error('Failed to load module');
  });
}
